import React, { useEffect, useRef, useState } from 'react';
import { 
  Monitor, 
  Smartphone, 
  Users, 
  Settings, 
  Database, 
  Activity,
  Bot,
  UserCheck,
  TrendingUp,
  Calendar,
  Shield,
  Brain,
  ChevronDown,
  Zap,
  BarChart3,
  FileText,
  Target,
  Clock,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Layers
} from 'lucide-react';

const AgentBlock = ({ title, items }) => (
  <div className="bg-white/60 backdrop-blur-sm rounded-lg p-3 border border-gray-200/50">
    <p className="text-xs font-semibold text-gray-700 mb-2">{title}</p>
    <div className="flex flex-wrap gap-1">
      {items.map((item, idx) => (
        <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
          {item}
        </span>
      ))}
    </div>
  </div>
);

const LayerCard = ({ layer, index, isVisible, isHighlighted }) => {
  const baseDelay = index * 150;
  
  return (
    <div 
      className={`
        relative transition-all duration-700 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
      `}
      style={{ transitionDelay: `${baseDelay}ms` }}
    >
      {/* Connector Arrow */}
      {index > 0 && (
        <div className="flex justify-center -mt-2 mb-2">
          <div className="flex flex-col items-center">
            <div className="w-px h-4 bg-gradient-to-b from-gray-300 to-gray-400"></div>
            <ChevronDown className="w-4 h-4 text-gray-400 -mt-1" />
          </div>
        </div>
      )}
      
      {/* Layer Card */}
      <div 
        className={`
          rounded-xl border p-6 md:p-8 transition-all duration-300
          ${isHighlighted 
            ? 'bg-gradient-to-r from-slate-800 to-slate-900 border-slate-700 shadow-xl shadow-slate-900/20' 
            : 'bg-white/80 backdrop-blur-sm border-gray-200 hover:border-gray-300 hover:shadow-md'
          }
        `}
      >
        {/* Layer Number Badge */}
        <div className="flex items-center gap-3 mb-4">
          <span className={`
            text-xs font-medium px-2 py-1 rounded-full
            ${isHighlighted ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-500'}
          `}>
            Layer {index + 1}
          </span>
          <div className={`
            p-2 rounded-lg
            ${isHighlighted ? 'bg-slate-700' : 'bg-gray-100'}
          `}>
            <layer.icon className={`w-5 h-5 ${isHighlighted ? 'text-cyan-400' : 'text-gray-600'}`} />
          </div>
        </div>

        {/* Title */}
        <h3 className={`
          text-xl md:text-2xl font-semibold mb-3
          ${isHighlighted ? 'text-white' : 'text-gray-900'}
        `}>
          {layer.title}
        </h3>

        {/* Description */}
        <p className={`
          leading-relaxed mb-4
          ${isHighlighted ? 'text-slate-300' : 'text-gray-600'}
        `}>
          {layer.description}
        </p>

        {/* Icons Row (for Layer 1) */}
        {layer.icons && (
          <div className="flex flex-wrap gap-4 mt-4">
            {layer.icons.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <item.icon className={`w-4 h-4 ${isHighlighted ? 'text-cyan-400' : 'text-gray-500'}`} />
                <span className={`text-sm ${isHighlighted ? 'text-slate-300' : 'text-gray-600'}`}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Agent Blocks (for Layer 3) */}
        {layer.agentGroups && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
            {layer.agentGroups.map((group, idx) => (
              <AgentBlock key={idx} title={group.title} items={group.items} />
            ))}
          </div>
        )}

        {/* Micro Text / Note */}
        {layer.note && (
          <p className={`
            text-sm mt-4 italic
            ${isHighlighted ? 'text-slate-400' : 'text-gray-500'}
          `}>
            {layer.note}
          </p>
        )}
      </div>
    </div>
  );
};

const AgenticArchitecture = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const layers = [
    {
      title: "Interface Layer",
      icon: Monitor,
      description: "Clients, cleaner-members, internal teams, and partners interact through web apps, mobile apps, SMS booking, and the Command Center operations dashboard.",
      icons: [
        { icon: Monitor, label: "Web / Mobile" },
        { icon: Smartphone, label: "Cleaner App" },
        { icon: Settings, label: "Admin Dashboard" },
        { icon: Zap, label: "API Integration" }
      ]
    },
    {
      title: "Event & Data Capture Layer",
      icon: Database,
      description: "Every action becomes a structured event — bookings, disputes, onboarding, performance metrics — logged into the operational database and event stream processor.",
      note: "All marketplace activity is structured, stored, and auditable."
    },
    {
      title: "Agentic Orchestration Layer",
      icon: Bot,
      description: "Autonomous AI agents manage recruitment, qualification, membership onboarding, content generation, pricing logic, intelligent dispatch, dispute structuring, quality control, and operational decisioning.",
      isHighlighted: true,
      agentGroups: [
        {
          title: "Supply Agents",
          items: ["Recruitment Funnel", "Qualification", "Membership", "Reliability Scoring", "Compliance"]
        },
        {
          title: "Growth Agents",
          items: ["SEO Content", "Conversion Optimization", "Demand Forecasting"]
        },
        {
          title: "Booking & Dispatch Agents",
          items: ["Intake Validation", "Pricing & Margin Protection", "Intelligent Dispatch", "Capacity Balancing", "Policy Routing"]
        },
        {
          title: "Client & Quality Agents",
          items: ["Dispute Structuring", "Evidence Collection", "Resolution Recommendation", "Quality Monitoring"]
        },
        {
          title: "Intelligence Agents",
          items: ["Workforce Modeling", "Time-on-Task Prediction", "Pricing Elasticity", "CLV Forecasting"]
        }
      ]
    },
    {
      title: "Humans-In-The-Loop",
      icon: UserCheck,
      description: "When defined risk thresholds are triggered: VIP clients, high refund risk, unassigned jobs, recurring disputes, safety flags — cases escalate to human support and operations teams for review, override, and refinement.",
      note: "Human correction continuously improves system intelligence."
    },
    {
      title: "Intelligence & Compounding Data",
      icon: Brain,
      description: "Axis accumulates proprietary datasets across workforce reliability, pricing elasticity, supply-demand forecasting, service duration accuracy, dispute patterns, and customer lifetime value.",
      note: "Each booking strengthens the system."
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 px-6 md:px-12 lg:px-24 relative overflow-hidden"
      style={{
        background: `
          linear-gradient(to bottom, #f8fafc, #f1f5f9),
          repeating-linear-gradient(
            0deg,
            transparent,
            transparent 50px,
            rgba(148, 163, 184, 0.05) 50px,
            rgba(148, 163, 184, 0.05) 51px
          ),
          repeating-linear-gradient(
            90deg,
            transparent,
            transparent 50px,
            rgba(148, 163, 184, 0.05) 50px,
            rgba(148, 163, 184, 0.05) 51px
          )
        `
      }}
    >
      {/* Subtle Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `
            radial-gradient(circle at 1px 1px, rgba(148, 163, 184, 0.15) 1px, transparent 0)
          `,
          backgroundSize: '24px 24px'
        }}
      />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <div className={`
          text-center mb-16 transition-all duration-700
          ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        `}>
          <div className="flex items-center justify-center gap-2 mb-4">
            <Layers className="w-6 h-6 text-slate-600" />
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
            Agentic Marketplace Architecture
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Axis operates as a coordinated Agentic operational engine where autonomous agents manage marketplace complexity and humans provide strategic oversight.
          </p>
        </div>

        {/* Architecture Stack */}
        <div className="space-y-2">
          {layers.map((layer, index) => (
            <LayerCard 
              key={index}
              layer={layer}
              index={index}
              isVisible={isVisible}
              isHighlighted={layer.isHighlighted}
            />
          ))}
        </div>

        {/* Bottom Closing Statement */}
        <div className={`
          mt-16 text-center transition-all duration-700 delay-1000
          ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        `}>
          <div className="bg-white/60 backdrop-blur-sm rounded-xl border border-gray-200 p-8 max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed font-medium">
              Axis Affordable ServiceTech Inc. is building the operational infrastructure layer for local services — an agentic coordination engine designed for scalable international expansion without proportional headcount growth.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgenticArchitecture;
